import * as THREE from 'three';
const { tan, cos, atan, acos, min, max, pow, PI, sqrt } = Math;
import { initCamera } from '../camera';
import decompose from '../utils/decompose';
import { getCamera, getCameraWorldTransform, getRect, } from '../../modules/player';
import { get, set } from '../../modules/scene';
import updateCameraLookAtTarget from '../utils/updateCameraLookAtTarget';
import getTargetWorldMatrix from '../utils/getTargetWorldMatrix';
import { merge, virtualTargetId } from '../../modules/sceneGraph';

const directionKeys = {
  w: 'FORWARD',
  a: 'LEFT',
  s: 'BACKWARD',
  d: 'RIGHT',
  ArrowUp: 'FORWARD',
  ArrowLeft: 'LEFT',
  ArrowDown: 'BACKWARD',
  ArrowRight: 'RIGHT',
};

const directions = {
  FORWARD: ['y', 1],
  BACKWARD: ['y', -1],
  LEFT: ['x', -1],
  RIGHT: ['x', 1],
};

const SPEED = 0.2;
const EASING_TIME = 2000;

export default function firstPersonMode(store, translator) {
  let oldOrbitMode = null;
  let velocity = new THREE.Vector3(0, 0, 0);
  const axes = {x: 0, y: 0, z: 0};
  let easingStart, easingTimer;

  function setVelocity(axis, factor) {
    axes[axis] += factor;
    velocity = new THREE.Vector3(axes.x, axes.y, axes.z).normalize().multiplyScalar(SPEED);
  }

  function updateVelocity() {
    easingTimer = Date.now() - easingStart;
    easingTimer = min(1, max(0.00001, easingTimer/EASING_TIME));
    const easedSpeed = -(easingTimer-1) * (easingTimer-1) + 1;
    velocity.normalize().multiplyScalar(SPEED*easedSpeed);
  }

  return {
    name: 'firstPersonMode',
    active:  false,
    enabled: true,
    options: {
      movementMode: 'fly',
      keyboardEnabled: true,
    },
    display: {
      playerTools: false,
    },

    heldKeys: [],

    tool: {

      keydown: function (ev) {
        const direction = directionKeys[ev.key];
        if (direction && this.active) {
          this.heldKeys.push(ev.key);
          const [axis, factor] = directions[direction];
          if(this.heldKeys.length<=1) easingStart = Date.now();
          setVelocity(axis, factor);
        }
      },

      keyup: function (ev) {
        const direction = directionKeys[ev.key];
        if (direction && this.active) {
          const idx = this.heldKeys.indexOf(ev.key);
          this.heldKeys.splice(idx, 1);
          const [axis, factor] = directions[direction];
          setVelocity(axis, -factor);
        }
      },

      keyheld: function (ev) {
        const firstKey = this.heldKeys[0];
        if(ev.key===firstKey && this.options.keyboardEnabled){
          updateVelocity();
          const movement = firstPersonMovement(store, velocity, this.options.movementMode);
          if (movement && translator.externals.vrControls) {
            translator.externals.vrControls.offset.add(movement);
          }
        }
      },
    },

    activate: function () {
      if(!oldOrbitMode) oldOrbitMode = get(store, { scene: true, plug: 'Player', property: 'orbitMode' });
      store.dispatch(set({ scene: true, plug: 'Player', property: 'orbitMode' }, 'firstPerson'));
    },

    deactivate: function () {
      store.dispatch(set({ scene: true, plug: 'Player', property: 'orbitMode' }, oldOrbitMode));
      oldOrbitMode = null;
    },

  };
}

const UP_POSITION = new THREE.Vector3(0, 1, 0);

function firstPersonMovement(store, direction, movementMode) {
  const playerData = get(store, { scene: true, evalPlug: 'Player' });
  const cameraId = getCamera(store);
  const cameraWorldMatrix = getCameraWorldTransform(store);
  const targetWorldMatrix = getTargetWorldMatrix(store, cameraId);
  const cameraData = get(store, { id:  cameraId, evalPlug: 'Camera' });

  if (targetWorldMatrix && cameraWorldMatrix && cameraData.upPosition) {
    const cameraPosition = new THREE.Vector3();
    const targetPosition = new THREE.Vector3();
    cameraPosition.setFromMatrixPosition(cameraWorldMatrix);
    targetPosition.setFromMatrixPosition(targetWorldMatrix);

    let forward = new THREE.Vector3().subVectors(targetPosition, cameraPosition).normalize();
    if (movementMode === 'plane') forward.projectOnPlane(UP_POSITION).normalize();

    const right = new THREE.Vector3().crossVectors(forward, UP_POSITION).normalize();

    const movement = new THREE.Vector3().addScaledVector(forward, direction.y).addScaledVector(right, direction.x);
    if (movement.y < -cameraPosition.y && playerData.constraintCameraY) movement.y = -cameraPosition.y;

    cameraPosition.add(movement);
    targetPosition.add(movement);

    cameraWorldMatrix.setPosition(cameraPosition);
    targetWorldMatrix.setPosition(targetPosition);

    updateCameraLookAtTarget(cameraWorldMatrix, targetWorldMatrix, cameraData.upPosition);

    store.dispatch(merge([cameraId, 'Transform', 0], decompose(cameraWorldMatrix)));
    if (!get(store, { id: cameraId, evalPlug: 'Transform' }).target) {
      store.dispatch(merge([virtualTargetId, 'Transform', 0], decompose(targetWorldMatrix)));
    }

    return movement;
  }
}



// WEBPACK FOOTER //
// ./src/translators/tools/firstPersonMode.js